import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { ApiCollectionResponse } from '../interfaces/responses/api-response';

/** Au-delà, la liste est relue à la navigation suivante. */
const FRESHNESS_MS = 60_000;

/**
 * Ce que le pack de l'élève ouvre dans son espace — `tests`, `library`,
 * `agenda`… (SKILL2ERA-137).
 *
 * <p>Les fonctionnalités ne sont pas dans le jeton Keycloak : elles se
 * décident en base, d'après le pack de la dernière inscription validée. Le
 * serveur les pose à la validation du jeton et les renvoie telles quelles par
 * `students/me/features` — le menu ne montre donc que ce que l'API acceptera.</p>
 *
 * <p>Le serveur applique un changement de pack dès la requête suivante ; la
 * liste est donc relue au plus une minute après la précédente, à chaque
 * navigation gardée, pour que le menu ne reste pas en retard d'une session.</p>
 *
 * <p>Le menu et le garde de route ne sont qu'un confort : c'est le serveur qui
 * refuse. Tant que la liste n'est pas connue, tout est ouvert ; un échec de
 * lecture garde la dernière liste connue et n'est pas mémorisé — la lecture
 * suivante réessaie.</p>
 */
@Injectable({ providedIn: 'root' })
export class StudentFeatureService {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.BASE_URL}/students/me/features`;

  private readonly features$ = new BehaviorSubject<ReadonlySet<string> | null>(null);
  private request$?: Observable<ReadonlySet<string> | null>;
  private requestedAt = 0;

  /** Émet à chaque liste qui change — le menu s'y recompose. */
  readonly changes$ = this.features$.asObservable();

  /** La liste à jour ; les appels rapprochés partagent la même lecture. */
  load(): Promise<ReadonlySet<string> | null> {
    if (!this.request$ || Date.now() - this.requestedAt > FRESHNESS_MS) {
      this.requestedAt = Date.now();
      this.request$ = this.http.get<ApiCollectionResponse<string>>(this.url).pipe(
        map((res) => new Set(res?.data ?? []) as ReadonlySet<string>),
        tap((features) => this.publish(features)),
        catchError(() => {
          this.request$ = undefined;
          return of(this.features$.value);
        }),
        shareReplay(1),
      );
    }
    return firstValueFrom(this.request$);
  }

  /**
   * La fonctionnalité est-elle ouverte ? Une liste se lit « l'une suffit » —
   * l'espace tests s'ouvre au diagnostic, à l'entraînement ou aux examens.
   * Sans liste connue, oui.
   */
  allows(feature: string | readonly string[] | undefined): boolean {
    if (!feature || feature.length === 0) return true;
    const features = this.features$.value;
    if (features === null) return true;
    return typeof feature === 'string' ? features.has(feature) : feature.some((f) => features.has(f));
  }

  private publish(next: ReadonlySet<string>): void {
    const current = this.features$.value;
    const same = current !== null && current.size === next.size && [...next].every((f) => current.has(f));
    if (!same) this.features$.next(next);
  }
}
